import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ContentLoader } from '@/components/feedback/ContentLoader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { useConfirm } from '@/components/ui';
import type { SocialProvider } from '@/features/auth/types/auth.types';
import { useAttachSocial, useDetachSocial, useSocialAccounts } from '../hooks/useSocialAccounts';

const GoogleIcon = (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 5.1 29.5 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.2-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 7.1 29.5 5 24 5 16 5 9.1 9.5 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 45c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 36 26.7 37 24 37c-5.3 0-9.7-2.6-11.3-7l-6.5 5C9 40.5 15.9 45 24 45z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.3 4.1-4.1 5.5l6.3 5.3C40.9 36.4 45 30.8 45 24c0-1.2-.1-2.3-.4-3.5z" />
  </svg>
);
const FacebookIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2" aria-hidden>
    <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
  </svg>
);
const AppleIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#000" aria-hidden>
    <path d="M16.36 1.43c.04 1.06-.36 2.1-1.06 2.86-.72.79-1.86 1.36-2.96 1.28-.05-1.02.4-2.08 1.05-2.78.74-.79 1.96-1.34 2.97-1.36zM20.5 17.2c-.5 1.16-.74 1.68-1.39 2.71-.9 1.44-2.18 3.23-3.76 3.24-1.4.02-1.76-.92-3.66-.9-1.9.01-2.3.92-3.7.9-1.58-.01-2.78-1.62-3.68-3.06C1.6 16.1 1.34 11.3 3.9 8.86c.94-.92 2.2-1.46 3.46-1.46 1.5 0 2.44.92 3.68.92 1.2 0 1.93-.92 3.66-.92 1.12 0 2.3.45 3.16 1.23-2.78 1.52-2.33 5.5.64 6.57z" />
  </svg>
);

const PROVIDERS: { id: SocialProvider; label: string; icon: ReactNode }[] = [
  { id: 'google', label: 'Google', icon: GoogleIcon },
  { id: 'facebook', label: 'Facebook', icon: FacebookIcon },
  { id: 'apple', label: 'Apple', icon: AppleIcon },
];

const ICON_BY_PROVIDER: Record<string, ReactNode> = {
  google: GoogleIcon,
  facebook: FacebookIcon,
  apple: AppleIcon,
};

/**
 * "Conturi atașate" — list/attach/detach social logins, mirroring the mobile
 * app's SocialAccounts screen. Attaching goes through the hosted auth flow
 * (redirect); listing + detaching are direct, Bearer-authenticated calls.
 */
export function AttachedAccountsPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const accounts = useSocialAccounts();
  const attach = useAttachSocial();
  const detach = useDetachSocial();

  // Banner reflecting the result of a just-completed attach redirect.
  const [banner, setBanner] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const attached = searchParams.get('attached');
    const attachError = searchParams.get('attach_error');
    if (!attached && !attachError) return;

    if (attached) {
      setBanner({ kind: 'ok', text: t('accounts.attached_ok', { provider: capitalize(attached) }) });
    } else if (attachError) {
      const map: Record<string, string> = {
        ineligible: t('accounts.error_ineligible'),
        provider: t('accounts.error_provider'),
        ticket: t('accounts.error_ticket'),
      };
      setBanner({ kind: 'error', text: map[attachError] ?? t('accounts.error_generic') });
    }
    // Strip the status params from the URL so a refresh doesn't re-show the banner.
    const next = new URLSearchParams(searchParams);
    next.delete('attached');
    next.delete('attach_error');
    setSearchParams(next, { replace: true });
    void accounts.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDetach = async (provider: string, email: string) => {
    const ok = await confirm({
      title: t('accounts.detach_title'),
      message: t('accounts.detach_confirm', { provider: provider.toUpperCase() }),
      confirmLabel: t('accounts.detach'),
      tone: 'danger',
    });
    if (!ok) return;
    detach.mutate(
      { provider, email },
      { onSuccess: () => setBanner({ kind: 'ok', text: t('accounts.detached_ok') }) },
    );
  };

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="text-2xl font-extrabold text-ink">{t('accounts.title')}</h1>

      {banner && (
        <div
          className={
            'mt-4 rounded-input px-4 py-3 text-sm ' +
            (banner.kind === 'ok' ? 'bg-[#ecfaec] text-[#21ae8c]' : 'bg-[#fff2ef] text-[#fd5f00]')
          }
        >
          {banner.text}
        </div>
      )}

      {/* About */}
      <div className="mt-5 rounded-card border border-accent/30 bg-accent/10 p-4">
        <p className="font-semibold text-accent">{t('accounts.about_title')}</p>
        <p className="mt-1 text-sm text-ink-soft">{t('accounts.about_text')}</p>
      </div>

      {/* Attached list */}
      <h2 className="mt-8 text-lg font-bold text-ink">{t('accounts.attached_title')}</h2>
      <div className="mt-3">
        {accounts.isLoading ? (
          <ContentLoader />
        ) : accounts.isError ? (
          <ErrorState description={t('common.load_error')} onRetry={() => void accounts.refetch()} />
        ) : accounts.data && accounts.data.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {accounts.data.map((acc) => (
              <li
                key={`${acc.provider}:${acc.email}`}
                className="flex items-center gap-3 rounded-card bg-surface p-4 shadow-card"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted-100">
                  {ICON_BY_PROVIDER[acc.provider] ?? null}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-ink">{String(acc.provider).toUpperCase()}</p>
                  <p className="truncate text-sm text-ink-mute">{acc.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => void onDetach(acc.provider, acc.email)}
                  disabled={detach.isPending}
                  className="rounded-input px-3 py-1.5 text-sm font-semibold text-[#e5484d] transition hover:bg-[#fff2ef] disabled:opacity-50"
                >
                  {t('accounts.detach')}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="rounded-card bg-surface p-4 text-sm text-ink-mute shadow-card">
            {t('accounts.empty')}
          </div>
        )}
      </div>

      {/* Attach new */}
      <h2 className="mt-8 text-lg font-bold text-ink">{t('accounts.attach_title')}</h2>
      <div className="mt-3 flex flex-col gap-2">
        {PROVIDERS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => attach.mutate(p.id)}
            disabled={attach.isPending}
            className="flex items-center justify-center gap-2.5 rounded-input border border-line bg-surface px-4 py-3 text-sm font-semibold text-ink transition hover:bg-muted-100 disabled:opacity-50"
          >
            {p.icon}
            {t('accounts.attach', { provider: p.label })}
          </button>
        ))}
      </div>
    </div>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
